import React, { useState } from 'react';
import { getDisplayCurrency } from '../utils/displayCurrency';
import PageMeta from '../components/PageMeta';
const DR_SYM = getDisplayCurrency().symbol;
import { Shield, Users, DollarSign, TrendingUp, Award, BarChart3, Lock, Activity, Download, LayoutDashboard, FileText, Gauge, Landmark, Lightbulb, Table2, Sparkles, ArrowUp } from 'lucide-react';
import useInvestorTelemetry from '../hooks/useInvestorTelemetry';
import DataRoomDocuments from '../components/DataRoomDocuments';
import DataRoomMayaWelcome from '../components/DataRoomMayaWelcome';
import DataRoomCodebase from '../components/DataRoomCodebase';
import DataRoomInvestorWelcome, { downloadShareCertificate } from '../components/DataRoomInvestorWelcome';
import DataRoomSyndicate from '../components/DataRoomSyndicate';
import TrafficAnalytics from '../components/TrafficAnalytics';
import { RoundsPlanCard } from '../components/investors/RoundsPlanCard';
import { InvestmentGrowthSlider } from '../components/investors/InvestmentGrowthSlider';

const TABS = [
  { id: 'story',     label: 'The Story' },
  { id: 'numbers',   label: 'The Numbers' },
  { id: 'proof',     label: 'The Proof' },
  { id: 'documents', label: 'Documents' },
];

const TAB_SECTIONS = {
  story:     ['sec-overview', 'sec-opportunity', 'sec-gtm', 'sec-competitors', 'sec-comparables', 'sec-thesis'],
  numbers:   ['sec-valuation', 'sec-simulator', 'sec-capital', 'sec-anchors', 'sec-tam', 'sec-dve', 'sec-fundraise-status', 'sec-revenue', 'sec-valfund', 'sec-brief', 'sec-syndicate'],
  proof:     ['sec-codebase', 'sec-command', 'sec-replicate', 'sec-milestones', 'sec-traffic', 'sec-pulse', 'sec-metrics', 'sec-kpi-history', 'sec-board'],
  documents: ['sec-documents-wrap'],
};
const ALL_TAB_IDS = Object.values(TAB_SECTIONS).flat();
const NAV_TAB_OF = Object.fromEntries(Object.entries(TAB_SECTIONS).flatMap(([t, ids]) => ids.map(id => [id, t])));

const NAV_SECTIONS = [
  { id: 'sec-overview',  label: 'Overview',        Icon: LayoutDashboard },
  { id: 'sec-documents', label: 'Documents',       Icon: FileText },
  { id: 'sec-valuation', label: 'Valuation',       Icon: Gauge },
  { id: 'sec-simulator', label: 'Your Investment', Icon: TrendingUp },
  { id: 'sec-capital',   label: 'Capital Path',    Icon: Landmark },
  { id: 'sec-anchors',   label: 'Revenue at Scale', Icon: Table2 },
  { id: 'sec-opportunity', label: 'The Opportunity', Icon: Sparkles },
  { id: 'sec-gtm',       label: 'Go-to-Market',     Icon: TrendingUp },
  { id: 'sec-tam',       label: 'Market Size',      Icon: Users },
  { id: 'sec-replicate', label: 'Cost to Replicate', Icon: DollarSign },
  { id: 'sec-command',   label: 'Command Centre',   Icon: Shield },
  { id: 'sec-competitors', label: 'vs Competition',  Icon: Award },
  { id: 'sec-comparables', label: 'Comparables',     Icon: Landmark },
  { id: 'sec-dve',       label: 'Valuation Engine', Icon: BarChart3 },
  { id: 'sec-thesis',    label: 'The Thesis',      Icon: Lightbulb },
  { id: 'sec-pulse',     label: 'Live Pulse',      Icon: Activity },
  { id: 'sec-traffic',   label: 'Web Traffic',     Icon: Activity },
  { id: 'sec-milestones', label: 'Milestones',     Icon: Award },
];

const ANCHOR_TIERS = [
  { name: 'Inflection',      color: '#7B4FD8' },
  { name: 'Early Traction',  color: '#6E58D0' },
  { name: 'Momentum',        color: '#5F63C6' },
  { name: 'Scale',           color: '#4E71B9' },
  { name: 'Breakout',        color: '#3C82A6' },
  { name: 'Expansion',       color: '#2A9190' },
  { name: 'Category Leader', color: '#1A9C73' },
  { name: 'Global',          color: '#0F8A4B' },
];

const OPP_CARDS = [
  { title: 'Building the Digital Safety Layer for the Next Generation', body: 'HiBFF delivers a highly defensive, end-to-end protected infrastructure that addresses the global crisis in minor-tech safeguarding. By shifting away from passive digital surveillance, our platform couples advanced multi-modal artificial intelligence with closed family-collaboration nodes, establishing a new category in scalable behavioral wellness tech.' },
  { title: 'Zero-Negligence Safeguarding & Filed IP Fortification', body: 'Built securely from the first line of code to clear institutional SOC2 audit validations. The runtime network deploys a highly customized Claude Sonnet 4.5 wrapper governed by rigorous programmatic filter blockers (NVIDIA NeMo Guardrails), blocking toxic interaction, medical/mental health queries, and self-harm prompts before execution. Covered by three live UK patent applications — GB2617027.4, GB2622892.4 and GB2622997.1 — all filed at the UK IPO.' },
  { title: 'HiBFF for Schools: A Frictionless, Zero-CAC B2B2C Acquisition Model', body: 'Capital efficiency driven by organic institutional distribution. HiBFF licenses schools worldwide on special institutional pricing — quoted per school (call for your rate) — with nothing for the school to administer beyond enrolment. Every licensed school introduces HiBFF to its parent community with maximum institutional credibility, driving families into the £28/mo UK price ($35.99 US) consumer ecosystem at near-zero CAC.' },
  { title: 'Membership for Life & Long-Term LTV Compounding', body: "Users onboard via Maya, an AI-avatar interface that guides them through a 22-question spoken psychometric mapping (BIG5/OCEAN matrix). HiBFF is a secure private space for life — teens create groups, invite friends and grow real friendships inside the platform, new tutors are added over time, and as members grow into adulthood they can recommend features, earning rewards when HiBFF adopts them. Earned rewards are secured in a 180-day countdown vault, anchoring a minimum 6-month lifecycle value ($159 contracted LTV at the $26.49 blended rate) per user block — the floor, not the ceiling, of a lifetime membership." },
];

const THESIS_EXTRACTS = [
  {
    title: 'Complete Regulatory Protection',
    tagline: 'Engineering out corporate negligence risk.',
    body: 'Minor-focused software assets face intense regulatory scrutiny globally regarding data management and safety. The custom Claude Sonnet 4.5 safety wrapper, automated 0.80 / 0.95 crisis triggers, and external helpline routing transform the application from a generic communication app into an institutional-grade, liability-free wellness asset.',
  },
  {
    title: 'Programmatic Proof of Low CAC',
    tagline: 'An unreplicable, hyper-efficient distribution engine.',
    body: 'The HiBFF Schools licensing protocol turns educational systems into the primary sales force: each licensed school introduces the brand directly to families with maximum institutional authority, driving localized parents into the £28/mo UK price ($35.99 US) ecosystem at a near-zero cost of customer acquisition.',
  },
  {
    title: 'Structural Data Moats vs Churn',
    tagline: 'Permanent switching costs built into the data structure.',
    body: 'A teen cannot easily switch to a competitor without throwing away a highly personalized psychometric identity built by Maya. The 90-day cooling pause and indefinite history playback mean the platform never loses custody of the user relationship — a high-probability reactivation pipeline protecting long-term LTV.',
  },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DataRoom = () => {
  const [passcode, setPasscode] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [deckDownload, setDeckDownload] = useState({ busy: false, err: null });
  const [sessionToken, setSessionToken] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [mayaState, setMayaState] = useState('intro');
  const [investorCeremony, setInvestorCeremony] = useState('intro');
  const [activeTab, setActiveTab] = useState('story');
  const [codebase, setCodebase] = useState(null);

  const changeTab = (tab) => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  React.useEffect(() => {
    if (!data) return;
    fetch(`${API_URL}/api/investors/public-snapshot`)
      .then(r => (r.ok ? r.json() : null))
      .then(s => setSnapshot(s))
      .catch(() => {});
  }, [data]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Disable print/download for prospect rooms
  React.useEffect(() => {
    if (data && data.room_type === 'prospect') {
      const style = document.createElement('style');
      style.id = 'no-print';
      style.textContent = '@media print { body { display: none !important; } }';
      document.head.appendChild(style);
      const handler = (e) => { if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 's')) { e.preventDefault(); } };
      document.addEventListener('keydown', handler);
      return () => { document.getElementById('no-print')?.remove(); document.removeEventListener('keydown', handler); };
    }
  }, [data]);

  const handleAccess = async (code) => {
    const c = code || passcode;
    if (!c.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/dataroom/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: c.trim() }),
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.detail || 'Access denied');
        setLoading(false);
        return;
      }
      const payload = await res.json();
      setSessionToken(payload.session_token || '');
      setData(payload);
    } catch {
      setError('Unable to connect. Please try again.');
    }
    setLoading(false);
  };

  // Founder preview hand-off: the vault's "Open Live Room" button stashes the
  // founder-preview passcode in localStorage so it never touches the URL.
  // The key is only consumed once the server actually responds — a fetch
  // aborted by an early page reload keeps the key so the retry succeeds.
  React.useEffect(() => {
    const code = localStorage.getItem('hibff_founder_preview_code');
    if (!code) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/dataroom/access`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ passcode: code.trim() }),
        });
        localStorage.removeItem('hibff_founder_preview_code');
        if (res.ok) {
          const payload = await res.json();
          setSessionToken(payload.session_token || '');
          setData(payload);
        }
      } catch { /* network abort — leave the key for the next mount */ }
    })();
  }, []);

  const handleDownloadBoardDecks = async () => {
    if (deckDownload.busy) return;
    setDeckDownload({ busy: true, err: null });
    try {
      const res = await fetch(`${API_URL}/api/dataroom/board-decks/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: passcode.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setDeckDownload({ busy: false, err: err.detail || `HTTP ${res.status}` });
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `HiBFF-Board-Decks-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setDeckDownload({ busy: false, err: null });
    } catch (e) {
      setDeckDownload({ busy: false, err: e.message || 'Download failed' });
    }
  };

  // Fire per-investor telemetry — captures dwell time on the Data Room as
  // a whole. Activates only when an investor_id is bound to the room.
  // Must be called unconditionally before any early-return to satisfy
  // Rules of Hooks.
  useInvestorTelemetry(
    data?.investor_id || (data?.room_id ? `prospect:${data.room_id}` : null),
    `data-room:${activeTab}`,
    !!data
  );

  if (!data) {
    return (
      <div className="min-h-screen bg-[#1A1209] flex items-center justify-center p-4">
        <PageMeta title="Private Data Room" path="/dataroom" noIndex />
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md text-center" data-testid="dataroom-gate">
          <img src="/hibff-logo.png" alt="HiBFF" className="h-14 w-auto mx-auto mb-4" data-testid="dataroom-gate-logo" />
          <h1 className="text-2xl font-black text-[#1A1209] mb-1">HiBFF Data Room</h1>
          <p className="text-xs text-gray-500 mb-6">Enter your access code to view investor materials.</p>
          <input type="text" value={passcode} onChange={e => setPasscode(e.target.value.toUpperCase())}
            placeholder="Enter passcode" maxLength={12}
            className="w-full px-4 py-3 text-center text-lg font-mono font-bold border-2 border-gray-200 rounded-xl focus:border-[#7B4FD8] focus:ring-2 focus:ring-[#7B4FD8]/20 tracking-widest"
            onKeyDown={e => e.key === 'Enter' && handleAccess()}
            data-testid="dataroom-passcode" />
          {error && <p className="text-xs text-red-500 mt-2" data-testid="dataroom-error">{error}</p>}
          <button onClick={() => handleAccess()} disabled={loading || !passcode.trim()}
            className="w-full mt-4 py-3 bg-[#7B4FD8] text-white font-semibold rounded-xl disabled:opacity-50 hover:bg-[#6B3FC8] transition-colors"
            data-testid="dataroom-submit">
            {loading ? 'Verifying...' : 'Access Data Room'}
          </button>
          <p className="text-[10px] text-gray-400 mt-4">This is a confidential investor data room. Unauthorized access is prohibited.</p>
        </div>
      </div>
    );
  }

  const m = data.metrics;
  const f = data.financials;
  const fr = data.fundraise;
  const v = data.valuation;
  const isInvestor = data.room_type === 'investor';

  return (
    <div className="min-h-screen bg-gray-100 select-none" data-testid="dataroom-content" onContextMenu={isInvestor ? undefined : (e) => e.preventDefault()}>
      {/* One-time investor welcome ceremony — the step-up moment (revisitable via gold tab) */}
      {isInvestor && (investorCeremony === 'revisit' || (investorCeremony === 'intro' && !data.investor_welcome_seen)) && (
        <DataRoomInvestorWelcome
          name={data.recipient}
          token={sessionToken}
          instrument={data.investor_instrument}
          staticMode={investorCeremony === 'revisit'}
          onEnter={() => setInvestorCeremony('closed')}
        />
      )}
      {/* Gold pull-down tab — rolls the investor welcome back down */}
      {isInvestor && !(investorCeremony === 'revisit' || (investorCeremony === 'intro' && !data.investor_welcome_seen)) && (
        <button
          onClick={() => setInvestorCeremony('revisit')}
          className="fixed top-0 left-1/2 -translate-x-1/2 z-[9997] flex items-center gap-1.5 px-4 pt-1 pb-1.5 rounded-b-xl text-[9px] font-black uppercase tracking-[0.15em] shadow-md transition-colors"
          style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D97A)', color: '#06251A' }}
          title="Revisit your investor welcome"
          data-testid="dr-investor-pulldown-tab"
        >
          Investor
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
        </button>
      )}
      {/* Maya greets prospects by name before the room opens */}
      {!isInvestor && mayaState !== 'closed' && (
        <DataRoomMayaWelcome
          name={data.recipient}
          token={sessionToken}
          staticMode={mayaState === 'revisit'}
          onEnter={() => setMayaState('closed')}
        />
      )}
      {/* Subtle pull-down tab — rolls Maya's front page back down (static, no voice) */}
      {!isInvestor && mayaState === 'closed' && (
        <button
          onClick={() => setMayaState('revisit')}
          className="fixed top-0 left-1/2 -translate-x-1/2 z-[9997] flex items-center gap-1.5 px-4 pt-1 pb-1.5 rounded-b-xl bg-[#1A1209]/85 text-white/70 hover:text-white hover:bg-[#1A1209] text-[9px] font-bold uppercase tracking-[0.15em] shadow-md transition-colors"
          title="Revisit Maya's welcome"
          data-testid="dr-maya-pulldown-tab"
        >
          Maya
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
        </button>
      )}
      {/* Dynamic Watermark for Prospect Rooms */}
      {!isInvestor && data.recipient && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, pointerEvents: 'none',
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', gap: '80px', padding: '40px',
        }} data-testid="watermark-overlay">
          {Array.from({ length: 20 }).map((_, i) => (
            <span key={i} style={{
              transform: 'rotate(-35deg)', color: 'rgba(0,0,0,0.04)', fontSize: '14px',
              fontWeight: 900, whiteSpace: 'nowrap', letterSpacing: '2px', userSelect: 'none',
            }}>CONFIDENTIAL — {data.recipient.toUpperCase()}</span>
          ))}
        </div>
      )}
      {/* Header */}
      <div className="bg-[#1A1209] text-white px-6 py-5">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/hibff-logo.png"
              alt="HiBFF"
              className="h-12 w-auto"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(123,79,216,0.35))' }}
              data-testid="dataroom-hibff-logo"
            />
            <div>
              <h1 className="text-xl font-black">HiBFF Data Room</h1>
              <p className="text-[10px] text-white/90">{data.room_name}{data.recipient ? ` — Prepared for ${data.recipient}` : ''}</p>
            </div>
          </div>
          <div className="text-right">
            {isInvestor ? (
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                <span className="text-[10px] text-emerald-400 font-semibold">Live Metrics</span>
              </div>
            ) : (
              <p className="text-[10px] text-white/85">Expires: {new Date(data.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            )}
            {isInvestor ? (
              <div className="flex flex-col items-end gap-1 mt-0.5">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide"
                  style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D97A)', color: '#06251A' }}
                  data-testid="dr-investor-badge">
                  Active Investor — Permanent Access
                </span>
                <button onClick={() => downloadShareCertificate(sessionToken)}
                  className="text-[10px] font-bold text-[#F5D97A] hover:text-white underline underline-offset-2 transition-colors"
                  data-testid="dr-certificate-download-header">
                  Investment Certificate ↓
                </button>
              </div>
            ) : (
              <p className="text-[10px] text-white/70 mt-0.5">Prospect Access — View Only</p>
            )}
          </div>
        </div>
      </div>

      {/* View-only notice for prospects */}
      {!isInvestor && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2">
          <p className="max-w-5xl mx-auto text-[10px] text-amber-700 font-semibold text-center">
            This data room is view-only. Downloading, printing, or screenshotting is not permitted.
          </p>
        </div>
      )}

      {/* Investor-only: Board Deck Archive download */}
      {isInvestor && data.board_decks_available > 0 && (
        <div className="max-w-5xl mx-auto px-6 pt-4" data-testid="investor-board-deck-archive">
          <div className="bg-gradient-to-r from-[#7B4FD8] to-[#5B32C8] rounded-2xl p-5 flex items-center gap-4 shadow-sm">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center text-white flex-shrink-0">
              <Download size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-white">Monthly Board Deck Archive</p>
              <p className="text-[11px] text-purple-100 mt-0.5">
                {data.board_decks_available} approved {data.board_decks_available === 1 ? 'deck' : 'decks'} · Print-ready HTML · Open any file → Print → Save as PDF
              </p>
              {deckDownload.err && <p className="text-[11px] text-rose-200 mt-1 font-semibold">{deckDownload.err}</p>}
            </div>
            <button onClick={handleDownloadBoardDecks} disabled={deckDownload.busy}
                    className="bg-white text-[#5B32C8] font-bold px-4 py-2 rounded-lg text-xs hover:bg-purple-50 transition-colors disabled:opacity-60 flex-shrink-0"
                    data-testid="download-board-decks-zip">
              {deckDownload.busy ? 'Preparing ZIP…' : 'Download ZIP'}
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 flex gap-6 items-start">
        {/* Left menu — desktop (filtered to active tab) */}
        <aside className="hidden lg:block w-52 flex-shrink-0 self-stretch" data-testid="dr-sidebar">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-3 space-y-1 sticky top-6">
            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.15em] px-2 pt-1 pb-2">{TABS.find(t => t.id === activeTab)?.label}</p>
            {NAV_SECTIONS.filter(({ id }) => (NAV_TAB_OF[id] || (id === 'sec-documents' ? 'documents' : null)) === activeTab).map(({ id, label, Icon }) => (
              <button key={id} onClick={() => scrollToSection(id)}
                className="w-full flex items-center gap-2.5 text-left px-2.5 py-2 rounded-lg text-xs font-semibold text-gray-600 hover:bg-purple-50 hover:text-[#7B4FD8] transition-colors"
                data-testid={`dr-nav-${id}`}>
                <Icon size={14} className="text-[#7B4FD8] flex-shrink-0" /> {label}
              </button>
            ))}
          </div>
        </aside>

        <div className="flex-1 min-w-0">
        {/* Hide sections that belong to other tabs */}
        <style>{ALL_TAB_IDS.filter(id => !TAB_SECTIONS[activeTab].includes(id)).map(id => `#${id}{display:none;}`).join('')}</style>
        {/* Tab bar + section pills */}
        <div className="sticky top-0 z-40 -mx-4 px-4 lg:mx-0 lg:px-0 py-2 bg-gray-100/95 backdrop-blur-sm mb-4" data-testid="dr-topnav">
          <div className="flex gap-1.5 mb-2" data-testid="dr-tabbar">
            {TABS.map(t => (
              <button key={t.id} onClick={() => changeTab(t.id)}
                className={`px-4 py-2 rounded-xl text-xs font-black transition-colors ${activeTab === t.id ? 'bg-[#7B4FD8] text-white shadow-sm' : 'bg-white border border-gray-200 text-gray-600 hover:text-[#7B4FD8] hover:border-[#7B4FD8]/40'}`}
                data-testid={`dr-tab-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {NAV_SECTIONS.filter(({ id }) => (NAV_TAB_OF[id] || (id === 'sec-documents' ? 'documents' : null)) === activeTab).map(({ id, label }) => (
              <button key={id} onClick={() => scrollToSection(id)}
                className="flex-shrink-0 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-[11px] font-bold text-gray-600 hover:border-[#7B4FD8]/50 hover:text-[#7B4FD8] transition-colors shadow-sm"
                data-testid={`dr-topnav-${id}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-6">
        {/* Personalised welcome — white card, single brand accent */}
        {data.recipient && (
          <div className="bg-white rounded-2xl border border-gray-200 border-l-4 border-l-[#7B4FD8] p-6 shadow-sm" data-testid="dr-welcome">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#7B4FD8] font-bold">Private Data Room</p>
            <h2 className="text-2xl font-black mt-1 text-[#1A1209]">Welcome, {String(data.recipient).split(' ')[0]}.</h2>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed max-w-2xl">
              This room was prepared personally for you. Explore the live numbers, open the diligence documents, and model your own investment below — everything here updates from HiBFF's real systems, not a static deck.
            </p>
          </div>
        )}

        {/* Company */}
        <div id="sec-overview" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16">
          <h2 className="text-lg font-black text-[#1A1209]">{data.company.name}</h2>
          <p className="text-xs text-[#7B4FD8] font-semibold uppercase tracking-wider mb-2">{data.company.tagline}</p>
          <p className="text-sm text-gray-600 leading-relaxed">{data.company.description}</p>
          <p className="text-[10px] text-gray-400 mt-2">Market: {data.company.market} — {data.company.website}</p>
        </div>

        {/* Diligence Document Library — view-only, watermarked */}
        <div id="sec-documents-wrap">
          <DataRoomDocuments token={sessionToken} />
          <div className="mt-4 bg-white rounded-2xl border border-gray-200 p-5 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="dr-whitepaper-card">
            <FileText size={26} className="text-[#7B4FD8] shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-black text-[#1A1209]">Published White Paper — The Warm Handover (Oct 2026)</p>
              <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                The published safety standard for AI conversations with young people, cited in the IP Premium of the
                Berkus valuation. Licensable to other platforms.
              </p>
            </div>
            <a href="/warm-handover?src=dataroom" target="_blank" rel="noopener noreferrer"
              className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#7B4FD8] hover:opacity-90 shrink-0"
              data-testid="dr-whitepaper-link">
              Read PDF
            </a>
          </div>
        </div>

        {/* ── Step 4: Prospect / Investor branched payload ───────────────── */}
        {data.valuation_benchmark && (
          <div id="sec-valuation" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-valuation-benchmark">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div>
                <h3 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em]">Asset-Backed Floor Valuation</h3>
                <p className="text-3xl font-black text-[#1A1209] mt-1" data-testid="dr-valuation-total">{data.valuation_benchmark.total_formatted}</p>
                <p className="text-[11px] text-gray-500">{data.valuation_benchmark.method}</p>
              </div>
              <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-[10px] font-bold uppercase">
                {isInvestor ? 'Full breakdown' : 'Headline only'}
              </span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed" data-testid="dr-valuation-framing">
              {data.valuation_benchmark.framing_text}
            </p>
            {isInvestor && data.valuation_benchmark.buckets && (
              <div className="mt-5 border-t border-gray-100 pt-4" data-testid="dr-valuation-buckets">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-3">Berkus Method — seven valuation buckets</h4>
                <div className="divide-y divide-gray-100">
                  {data.valuation_benchmark.buckets.map((b) => (
                    <div key={b.id} className="py-3 flex items-start justify-between gap-4" data-testid={`dr-valuation-bucket-${b.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900">{b.label}</p>
                        {b.evidence && b.evidence.length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {b.evidence.map((ev, i) => (
                              <li key={i} className="text-[11px] text-gray-600 leading-snug">· {ev}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <span className="text-sm font-black text-gray-900 shrink-0">${b.value.toLocaleString('en-US')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Your Investment — interactive simulator (live from public snapshot) ── */}
        {snapshot && (
          <div id="sec-simulator" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-simulator">
            <InvestmentGrowthSlider
              floor={Number(snapshot?.valuation_usd) || 11500000}
              totalShares={Number(snapshot?.total_shares) || 10000000}
              perUser={Number(snapshot?.monthly_per_user_usd) || 26.49}
              multiple={Number(snapshot?.revenue_multiple) || 9}
              anchors={snapshot?.user_anchors?.length ? snapshot.user_anchors : undefined}
                            variant="public"
            />
          </div>
        )}

        {/* ── Two-step capital path (live-priced from cap table) ── */}
        {(snapshot?.rounds_plan || []).length > 0 && (
          <div id="sec-capital" className="scroll-mt-16" data-testid="dr-capital-path">
            <RoundsPlanCard plan={snapshot.rounds_plan} variant="public" />
          </div>
        )}

        {/* ── Anchor Reference Matrix — revenue at scale ── */}
        {snapshot?.user_anchors?.length > 0 && (
          <div id="sec-anchors" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-anchor-matrix">
            <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1">Revenue at Scale — Anchor Reference Matrix</h3>
            <p className="text-[11px] text-gray-500 mb-4">
              User milestone → MRR → ARR → valuation at {Number(snapshot?.revenue_multiple) || 9}× multiple, computed live at ${Number(snapshot?.monthly_per_user_usd) || 26.49}/family/month blended (UK price £28/mo = $35.99 US · £1 ≈ $1.29, Oct 2026) at an illustrative multiple. Indicative — not an offer of securities, not financial guidance.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                    <th className="text-left py-2 pr-3 font-bold">User Anchor</th>
                    <th className="text-left py-2 px-3 font-bold">Monthly (MRR)</th>
                    <th className="text-left py-2 px-3 font-bold">Annual (ARR)</th>
                    <th className="text-left py-2 pl-3 font-bold">Valuation @ {Number(snapshot?.revenue_multiple) || 12}×</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const perUser = Number(snapshot?.monthly_per_user_usd) || 26.49;
                    const mult = Number(snapshot?.revenue_multiple) || 12;
                    const floor = Number(snapshot?.valuation_usd) || 11500000;
                    let flagged = false;
                    return snapshot.user_anchors.map((u, i) => {
                      const users = Number(u) || 0;
                      const mrr = users * perUser;
                      const arr = mrr * 12;
                      const val = arr * mult;
                      const unclamps = !flagged && val > floor;
                      if (unclamps) flagged = true;
                      const tier = ANCHOR_TIERS[Math.min(i, ANCHOR_TIERS.length - 1)];
                      return (
                        <tr key={i} className="border-b border-gray-50" data-testid={`dr-anchor-row-${i}`}>
                          <td className="py-2.5 pr-3 font-bold text-gray-800">
                            <span className="inline-flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: tier.color }} />
                              {users.toLocaleString('en-US')} Users
                              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wide" style={{ background: `${tier.color}22`, color: tier.color }}>{tier.name}</span>
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-left text-gray-600">${mrr.toLocaleString('en-US')} / mo</td>
                          <td className="py-2.5 px-3 text-left text-gray-600">${arr.toLocaleString('en-US')} / yr</td>
                          <td className="py-2.5 pl-3 text-left font-black" style={{ color: tier.color }}>
                            {unclamps && <span className="mr-2 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase whitespace-nowrap">Unclamps from floor</span>}
                            ${val.toLocaleString('en-US')}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── The Opportunity — verbatim from the public investor document ── */}
        <div id="sec-opportunity" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-opportunity">
          <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1 flex items-center gap-2"><Sparkles size={14} className="text-[#7B4FD8]" /> The Opportunity</h3>
          <p className="text-[11px] text-gray-500 mb-4">As published in the HiBFF investor document at hibff.com/investors.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {OPP_CARDS.map((c, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4 hover:border-[#7B4FD8]/30 transition-colors" data-testid={`dr-opp-card-${i}`}>
                <p className="text-xs font-black text-[#1A1209] mb-1.5">{c.title}</p>
                <p className="text-[11px] text-gray-600 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Go-to-Market — Point of Entry ── */}
        <div id="sec-gtm" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-gtm">
          <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1 flex items-center gap-2"><TrendingUp size={14} className="text-[#F5831F]" /> Go-to-Market — Point of Entry</h3>
          <p className="text-[11px] text-gray-500 mb-4">Families first through social channels; schools follow through a two-fold civic and trial-led motion.</p>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="border border-gray-100 rounded-xl p-4 hover:border-[#7B4FD8]/30 transition-colors" data-testid="dr-gtm-families">
              <p className="text-xs font-black text-[#1A1209] mb-1">1 · Families — Social Media &amp; Family Groups</p>
              <p className="text-[11px] text-gray-600 leading-relaxed">Point of entry is social media and family groups, reaching parents and teens directly. The funnel is consent-first by design: the teen registers, a confirmation email goes to the parent or guardian, and it is the parent/guardian who confirms the account, chooses the subscription and pays. Safeguarding and the paying customer are built into the same step.</p>
            </div>
            <div className="border border-gray-100 rounded-xl p-4 hover:border-[#7B4FD8]/30 transition-colors" data-testid="dr-gtm-schools">
              <p className="text-xs font-black text-[#1A1209] mb-1">2 · Schools — A Two-Fold Motion (longer conversion)</p>
              <p className="text-[11px] text-gray-600 leading-relaxed">Inspired in part by Undercover Boss: <strong>direct to Mayors</strong> — going undercover in disadvantaged cities of up to 190k residents, tracked live in the Command Centre&apos;s Schools Outreach engine — and <strong>direct to broader schools</strong> via a simple 14-day free trial converting to a flat rate per student on terms negotiated per school. A second channel runs in partnership with education distributors.</p>
            </div>
          </div>
          <p className="text-[11px] text-gray-700 mt-4 leading-relaxed border-l-4 border-[#F5831F]/40 pl-3"><strong>The flywheel:</strong> teens and parents drive energy and curiosity into the education sector — consumer demand pulls school adoption, not the other way round.</p>
        </div>

        {/* ── Market Size — TAM/SAM by region (founder estimates from public population data) ── */}
        <div id="sec-tam" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-tam">
          <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1">Market Size — TAM / SAM by Region</h3>
          <p className="text-[11px] text-gray-500 mb-4">Teens aged 13–19 · consumer blended revenue $26.49/mo ($318/yr; UK headline price £28/mo = $35.99 US) · schools on special institutional pricing (quoted per school). Founder estimates from public population statistics — indicative, not guidance.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                <th className="text-left py-2 pr-3 font-bold">Region</th><th className="text-left py-2 px-3 font-bold">Teens 13–19</th><th className="text-left py-2 px-3 font-bold">Consumer TAM /yr</th><th className="text-left py-2 pl-3 font-bold">SAM (1% capture) /yr</th>
              </tr></thead>
              <tbody>
                {[
                  ['United Kingdom', 5.6, '#7B4FD8'], ['United States', 30.1, '#2563EB'], ['European Union', 30.8, '#0891B2'],
                  ['Canada + Australia + NZ', 6.3, '#059669'], ['Middle East (GCC)', 7.0, '#D97706'],
                ].map(([region, m, color], i) => (
                  <tr key={i} className="border-b border-gray-50" data-testid={`dr-tam-row-${i}`}>
                    <td className="py-2.5 pr-3 font-bold text-gray-800"><span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />{region}</span></td>
                    <td className="py-2.5 px-3 text-left text-gray-600">{m}M</td>
                    <td className="py-2.5 px-3 text-left text-gray-600">${(m * 318 / 1000).toFixed(1)}B</td>
                    <td className="py-2.5 pl-3 text-left font-black" style={{ color }}>${(m * 318 * 0.01 * 1000).toFixed(0)}M</td>
                  </tr>
                ))}
                <tr className="bg-gray-50" data-testid="dr-tam-total">
                  <td className="py-2.5 pr-3 font-black text-gray-900">Launch corridor total</td>
                  <td className="py-2.5 px-3 text-left font-black text-gray-900">79.8M</td>
                  <td className="py-2.5 px-3 text-left font-black text-gray-900">$25.4B</td>
                  <td className="py-2.5 pl-3 text-left font-black text-[#7B4FD8]">$254M</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Cost to Replicate ── */}
        <div id="sec-replicate" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-replicate">
          <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1">What Would It Cost to Replicate HiBFF?</h3>
          <p className="text-[11px] text-gray-500 mb-4">Independent agency benchmark to rebuild HiBFF.com in its entirety — platform, Command Centre, AI safety stack, mobile apps and integrations.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-4"><p className="text-xl font-black text-rose-600">$1,875,000</p><p className="text-[10px] text-gray-500 uppercase font-bold mt-1">Agency build — lower bound</p></div>
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-4"><p className="text-xl font-black text-rose-600">$2,600,000</p><p className="text-[10px] text-gray-500 uppercase font-bold mt-1">Agency build — upper bound</p></div>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4"><p className="text-xl font-black text-amber-600">2+ Years</p><p className="text-[10px] text-gray-500 uppercase font-bold mt-1">Delivery time — before first user</p></div>
          </div>
          <p className="text-[11px] text-gray-600 mt-3 leading-relaxed">A competitor starting today spends $1.9–2.6M and two-plus years just to reach where HiBFF already stands — then still faces the patent wall, the schools distribution engine, and the psychometric data moat. The $11.5M valuation floor is anchored beneath replacement cost plus the assets money alone cannot buy.</p>
        </div>

        {/* ── The Command Centre ── */}
        <div id="sec-command" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-command">
          <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1 flex items-center gap-2"><Shield size={14} className="text-[#7B4FD8]" /> The Command Centre — Built Future-Proof</h3>
          <p className="text-[11px] text-gray-500 mb-4">Designed, architected and coded by the founder: a single operating surface that runs the entire business — and everything you are reading in this room is generated live from it.</p>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              ['Investor Operations', 'Secure Vault with 2FA, investor CRM, passcode data rooms with per-prospect telemetry (what you opened, for how long, from where), automated Monday Investor Pulse, live cap-table and valuation engine.'],
              ['NASDAQ-Grade Readiness', 'Audit-ready reporting spine: live codebase metrics, milestone ledger, diligence library with version control, KPI history and board-deck archive — the reporting discipline of a listed company, years early.'],
              ['Growth Engines Ready to Scale', 'Automated lead generation system with built-in auto-responder and social posts. B2B leads pipeline (1,114 leads), Mayor Outreach Tracker across 60 cities, schools licensing engine, corporate and family programmes — each one an identified opportunity a new owner could pursue in the event of a trade sale.'],
              ['Full-Stack Safety Governance', 'Crisis triage thresholds, safety filter administration, frontend crash telemetry, bot protection and GDPR/KOSA tooling — operable by a non-technical owner from one screen.'],
            ].map(([t, b], i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4" data-testid={`dr-command-card-${i}`}>
                <p className="text-xs font-black text-[#1A1209] mb-1">{t}</p>
                <p className="text-[11px] text-gray-600 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Codebase — Live Breakdown (technical diligence) ── */}
        <DataRoomCodebase token={sessionToken} onMetrics={setCodebase} />

        {/* ── HiBFF vs the competition — four-category blue ocean ── */}
        <div id="sec-competitors" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-competitors">
          <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1">A Blue Ocean Position — Four Categories, No Carbon Copy</h3>
          <p className="text-[11px] text-gray-500 mb-4">HiBFF V3 spans four distinct markets — Consumer AI, Youth SEL, EdTech/Schools, and Web3 gamified incentives — so it has no single direct competitor. Its landscape splits across four categories:</p>
          <div className="grid md:grid-cols-2 gap-3 mb-6">
            {[
              ['1 · Consumer AI Companions — the unsafe category', 'Character.ai · Replika · Nomi.ai · Kindroid', 'Both leaders face public and legal backlash over safety failures, grooming risks and mental-health crises. HiBFF runs a multi-stage safety pipeline (NeMo Guardrails, lexicon filtering, crisis escalation to Samaritans) — pairing healthy real-world practice with a secure private space teens keep for life.'],
              ['2 · EdTech & SEL Platforms', 'EVERFI Character Playbook · Second Step · Panorama · Classcraft', 'Static videos and multiple-choice quizzes teens find boring, locked behind school procurement. HiBFF offers Socratic AI practice partners adapted to each teen\u2019s BIG5/OCEAN profile — and bridges schools (B2B) and families (B2C) in one hybrid model.'],
              ['3 · Parental Control & Youth Safety', 'Bark · Qustodio · Aura · Life360', 'Banning, controlling and confiscating teaches teens to hide behaviour, not build maturity. HiBFF\u2019s Parent-Teen Privacy Bridge gives parents progress metrics while raw chats stay private — trust, not surveillance.'],
              ['4 · Gamified Habit & Wellness Apps', 'Habitica · Finch · SuperBetter', 'Self-reported checkboxes are easily gamed. HiBFF delivers cryptographically backed rewards: a 180-day verification vault, dual parent-teen sign-offs, and Crossmint NFT minting.'],
            ].map(([t, k, b], i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4" data-testid={`dr-comp-cat-${i}`}>
                <p className="text-xs font-black text-[#1A1209]">{t}</p>
                <p className="text-[10px] text-[#7B4FD8] font-semibold mt-0.5 mb-1.5">{k}</p>
                <p className="text-[11px] text-gray-600 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] font-black text-gray-700 uppercase tracking-wider mb-2">The Competitive Matrix</p>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead><tr className="text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                <th className="text-left py-2 pr-2 font-bold">Feature / Category</th>
                <th className="text-left py-2 px-2 font-bold">Character.ai / Replika</th>
                <th className="text-left py-2 px-2 font-bold">Bark / Qustodio</th>
                <th className="text-left py-2 px-2 font-bold">Traditional SEL</th>
                <th className="text-left py-2 px-2 font-black text-[#7B4FD8]">HiBFF.com V3</th>
              </tr></thead>
              <tbody>
                {[
                  ['Real-Time AI Guardrails', '\u274C Unsafe', 'N/A', 'N/A', '\u2705 5-Stage Pipeline'],
                  ['Parent-Teen Privacy Bridge', '\u274C None', '\u274C Surveillance', 'N/A', '\u2705 Anonymized Progress'],
                  ['Real-World Practice Focus', '\u274C Screen Trap', '\u274C Restriction', '\u26A0\uFE0F Static', '\u2705 Socratic Micro-Goals'],
                  ['BIG5 / OCEAN Personalization', '\u274C None', 'N/A', '\u274C Generic', '\u2705 Dynamic Matching'],
                  ['Verifiable NFT Vault Rewards', '\u274C None', 'N/A', 'N/A', '\u2705 180-Day Escrow'],
                ].map((row, i) => (
                  <tr key={i} className="border-b border-gray-50" data-testid={`dr-comp-row-${i}`}>
                    <td className="py-2.5 pr-2 text-gray-800 font-bold">{row[0]}</td>
                    {row.slice(1, 4).map((v, j) => (
                      <td key={j} className={`text-left py-2.5 px-2 ${v === 'N/A' ? 'text-gray-300' : 'text-gray-500'}`}>{v}</td>
                    ))}
                    <td className="text-left py-2.5 px-2 font-black text-emerald-600 bg-emerald-50/60">{row[4]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-700 mt-4 leading-relaxed border-l-4 border-[#7B4FD8]/30 pl-3"><strong>Conclusion:</strong> HiBFF V3 has no direct competitor that combines safe conversational AI, personality-matched social challenges, privacy-first parental visibility, and blockchain-verified incentives into a single platform.</p>
        </div>

        {/* ── Market Comparables & Exit Landscape ── */}
        <div id="sec-comparables" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-comparables">
          <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1 flex items-center gap-2"><Landmark size={14} className="text-[#7B4FD8]" /> Market Comparables &amp; Exit Landscape</h3>
          <p className="text-[11px] text-gray-500 mb-4">The companies operating in HiBFF&apos;s four adjacent categories — what they built, how long it took, and what the market paid for them.</p>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              ['Character.AI', 'Consumer Conversational AI', '~5 Years (Inc. Nov 2021)', '20M+ MAUs, 69M+ App Downloads', '~$32M Revenue', '~$1.0B (Peak $2.5B in 2024)', false, 'Secured $150M Series A; $2.5B valuation peak'],
              ['Panorama Education', 'K-12 SEL & Student Analytics', '~14 Years (Founded 2012)', '1,500+ School Districts (~15M+ Students)', '~$60M–$80M Annual Revenue', '~$1.0B+ (Private Equity Backed)', true, '$1B Unicorn — acquired by General Atlantic in 2021'],
              ['EVERFI', 'B2B School Digital Education & SEL', '~18 Years (Founded 2008)', '60,000+ Schools, 5M+ Students/yr', '~$120M Annual Revenue', '$750M (Acquired)', false, 'Near $1B exit — acquired by Blackbaud for $750M cash in 2021'],
              ['Life360 (Owner of Bark/Tile)', 'Family Safety & Location Tracking', '~18 Years (Founded 2008)', '66M+ Active Monthly Users', '~$305M Annual Revenue', '~$2.8B (Public NASDAQ: LIF)', true, 'Public $1B+ cap — traded enterprise over $2.8B'],
              ['Second Step (Committee for Children)', 'K-12 SEL Curriculum', '~45 Years (Founded 1979)', '26,000+ Schools (~14M+ Students)', '~$35.8M Annual Non-Profit Revenue', 'N/A (Non-Profit Organization)', false, 'Non-profit entity'],
              ['Classcraft', 'Gamified Classroom Learning', '~13 Years (Founded 2013)', '10M+ Teachers & Students', '~$5M–$10M ARR', 'Acquired by HMH (Price Undisclosed)', false, 'Acquired by Houghton Mifflin Harcourt in 2023'],
            ].map(([name, sector, time, scale, rev, val, hit, exit], i) => (
              <div key={i} className={`rounded-xl border p-4 ${hit ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-100'}`} data-testid={`dr-comparable-card-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="min-w-0">
                    <p className="text-xs font-black text-[#1A1209]">{name}</p>
                    <p className="text-[10px] text-[#7B4FD8] font-semibold mt-0.5">{sector}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wide flex-shrink-0 ${hit ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    {hit ? '$1B+ Exit / Cap' : 'No $1B+ Exit'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">
                  <div><p className="text-[9px] text-gray-400 uppercase font-bold">Time in Business</p><p className="text-[11px] text-gray-700 font-semibold">{time}</p></div>
                  <div><p className="text-[9px] text-gray-400 uppercase font-bold">Scale / Users</p><p className="text-[11px] text-gray-700 font-semibold">{scale}</p></div>
                  <div><p className="text-[9px] text-gray-400 uppercase font-bold">Revenue</p><p className="text-[11px] text-gray-700 font-semibold">{rev}</p></div>
                  <div><p className="text-[9px] text-gray-400 uppercase font-bold">Valuation</p><p className={`text-[11px] font-black ${hit ? 'text-emerald-700' : 'text-gray-800'}`}>{val}</p></div>
                </div>
                <p className="text-[10px] text-gray-500 mt-3 pt-2 border-t border-gray-100">{exit}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-700 mt-4 leading-relaxed border-l-4 border-[#7B4FD8]/30 pl-3"><strong>Read:</strong> Each of HiBFF&apos;s four categories has already produced billion-dollar outcomes — Panorama and Life360 crossed $1B, EVERFI exited at $750M cash — yet no company in this table combines all four categories. HiBFF sits at that intersection.</p>
        </div>

        {/* ── Dynamic Valuation Engine — same model as the Cap Table ── */}
        <div id="sec-dve" className="rounded-2xl p-6 shadow-sm scroll-mt-16 text-white" style={{ background: 'linear-gradient(135deg, #2D1B69 0%, #4C2A9E 60%, #5B32C8 100%)' }} data-testid="dr-dve">
          <h3 className="text-sm font-black uppercase tracking-wider mb-1 flex items-center gap-2"><BarChart3 size={14} className="text-purple-300" /> Dynamic Valuation Engine</h3>
          <p className="text-[11px] text-purple-200 mb-4">The exact model behind the cap table — an asset-backed floor that unclamps into a revenue multiple as users scale.</p>
          <div className="bg-white/10 rounded-xl px-4 py-3 mb-4 font-mono text-sm font-bold text-center" data-testid="dr-dve-formula">
            V<sub>E</sub> = max( V<sub>floor</sub> , ARR × {Number(snapshot?.revenue_multiple) || 12} )
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-2xl font-black">${((Number(snapshot?.valuation_usd) || 11500000) / 1000000).toFixed(1)}M</p>
              <p className="text-[10px] text-purple-200 uppercase font-bold">Asset-Backed Floor</p>
              <p className="text-[10px] text-purple-300 mt-1">Replacement cost: {codebase?.totals?.everything?.lines ? `${Math.round(codebase.totals.everything.lines / 1000)}k` : '300k+'} lines of SOC2-ready code (recounted live), 3 live UK patent applications, AI safety wrappers</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-2xl font-black">{Math.ceil((Number(snapshot?.valuation_usd) || 11500000) / ((Number(snapshot?.monthly_per_user_usd) || 26.49) * 12 * (Number(snapshot?.revenue_multiple) || 12))).toLocaleString()} Users</p>
              <p className="text-[10px] text-purple-200 uppercase font-bold">Inflection Boundary</p>
              <p className="text-[10px] text-purple-300 mt-1">The point where ARR × {Number(snapshot?.revenue_multiple) || 12} exceeds the floor and the formula unclamps</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-2xl font-black">{Number(snapshot?.revenue_multiple) || 12}× ARR (illustrative)</p>
              <p className="text-[10px] text-purple-200 uppercase font-bold">Scaling Multiple</p>
              <p className="text-[10px] text-purple-300 mt-1">Illustrative recurring-revenue framework at ${Number(snapshot?.monthly_per_user_usd) || 26.49} blended revenue per paying family/month (UK price £28/mo = $35.99 US)</p>
            </div>
          </div>
        </div>

        {/* ── The Investment Thesis — extracts from the public investor document ── */}
        <div id="sec-thesis" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-thesis">
          <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1 flex items-center gap-2"><Lightbulb size={14} className="text-[#F5831F]" /> The Investment Thesis</h3>
          <p className="text-[11px] text-gray-500 mb-4">Extracts from the HiBFF investor document — the full version is public at hibff.com/investors.</p>
          <div className="grid md:grid-cols-3 gap-4">
            {THESIS_EXTRACTS.map((t, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4 hover:border-[#7B4FD8]/30 transition-colors" data-testid={`dr-thesis-card-${i}`}>
                <p className="text-xs font-black text-[#1A1209]">{t.title}</p>
                <p className="text-[10px] text-[#7B4FD8] font-semibold mt-0.5 mb-2">{t.tagline}</p>
                <p className="text-[11px] text-gray-600 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>

        {data.traffic && (
          <div id="sec-traffic" className="scroll-mt-16" data-testid="dr-traffic">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm mb-4">
              <h3 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] flex items-center gap-2"><Activity size={12} /> Live Web Traffic — last {data.traffic.days} days</h3>
              <p className="text-xs text-gray-500 mt-1">Counted live from our first-party, cookieless analytics beacon the moment you opened this room.</p>
            </div>
            <TrafficAnalytics data={data.traffic} testidPrefix="dr-traffic" />
          </div>
        )}

        {data.pulse_condensed && (
          <div id="sec-pulse" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-pulse">
            <h3 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3 flex items-center gap-2"><Activity size={12} /> {isInvestor ? 'Live Pulse' : 'Investor Pulse — a sample of what Active Investors receive every Monday morning'}</h3>
            <div className="grid grid-cols-3 gap-3" data-testid="dr-pulse-condensed">
              <div className="text-center bg-gray-50 rounded-xl p-3">
                <p className="text-2xl font-black text-[#1A1209]">{data.pulse_condensed.active_now}</p>
                <p className="text-[9px] text-gray-500 uppercase font-semibold">Active now</p>
              </div>
              <div className="text-center bg-gray-50 rounded-xl p-3">
                <p className="text-2xl font-black text-[#1A1209]">{data.pulse_condensed.signups_7d}</p>
                <p className="text-[9px] text-gray-500 uppercase font-semibold">Signups (7d)</p>
              </div>
              <div className="text-center bg-gray-50 rounded-xl p-3">
                <p className="text-2xl font-black text-[#1A1209]">{data.pulse_condensed.conversations_7d}</p>
                <p className="text-[9px] text-gray-500 uppercase font-semibold">Conversations (7d)</p>
              </div>
            </div>
            {isInvestor && data.pulse_live && (
              <div className="grid grid-cols-3 gap-3 mt-3 border-t border-gray-100 pt-3" data-testid="dr-pulse-live">
                <div className="text-center bg-emerald-50 rounded-xl p-3">
                  <p className="text-2xl font-black text-emerald-700">{data.pulse_live.total_users}</p>
                  <p className="text-[9px] text-emerald-700 uppercase font-semibold">Total users</p>
                </div>
                <div className="text-center bg-emerald-50 rounded-xl p-3">
                  <p className="text-2xl font-black text-emerald-700">{data.pulse_live.paid_users}</p>
                  <p className="text-[9px] text-emerald-700 uppercase font-semibold">Paid users</p>
                </div>
                <div className="text-center bg-emerald-50 rounded-xl p-3">
                  <p className="text-2xl font-black text-emerald-700">{data.pulse_live.sessions_total}</p>
                  <p className="text-[9px] text-emerald-700 uppercase font-semibold">Sessions total</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recent milestones (Prospect = last 5; Investor = full) */}
        {(data.milestones_recent_5 || data.milestones_full) && (
          <div id="sec-milestones" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-milestones-split">
            <h3 className="text-sm font-bold text-[#1A1209] mb-3 flex items-center gap-2">
              <Award size={14} className="text-[#F5831F]" />
              {isInvestor ? 'Milestones (full)' : 'Latest milestones (last 5)'}
            </h3>
            <div className="space-y-2">
              {(isInvestor
                ? (data.milestones_full || data.milestones_recent_5 || [])
                : (data.milestones_recent_5 || [])
              ).map((ms, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`dr-milestone-${i}`}>
                  <span className="w-2 h-2 bg-[#F5831F] rounded-full flex-shrink-0" />
                  <span className="text-xs text-gray-800 flex-1">{ms.title}</span>
                  <span className="text-[10px] text-gray-400">{ms.date}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Investor / fundraise status (Step 4 correction) — investor only.
            Hidden temporarily if neither investors nor rounds exist, rather
            than showing semantically wrong content. */}
        {isInvestor && data.fundraise_status && (
          ((data.fundraise_status.investors && data.fundraise_status.investors.length > 0) ||
           (data.fundraise_status.rounds && data.fundraise_status.rounds.length > 0)) && (
          <div id="sec-fundraise-status" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-fundraise-status">
            <h3 className="text-sm font-bold text-[#1A1209] mb-3 flex items-center gap-2"><TrendingUp size={14} className="text-[#7B4FD8]" /> Investor &amp; fundraise status</h3>

            {data.fundraise_status.rounds && data.fundraise_status.rounds.length > 0 && (
              <div className="mb-4">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Rounds</h4>
                <div className="divide-y divide-gray-100">
                  {data.fundraise_status.rounds.map((r, i) => (
                    <div key={i} className="py-2 flex items-center justify-between text-xs" data-testid={`dr-round-${i}`}>
                      <div>
                        <p className="font-semibold text-gray-900">{r.round_name || '—'}</p>
                        <p className="text-[10px] text-gray-500">
                          Target ${(r.target_raise || 0).toLocaleString('en-US')}
                          {r.pre_money_valuation ? ` · Pre-money $${(r.pre_money_valuation || 0).toLocaleString('en-US')}` : ''}
                          {r.target_close_date ? ` · Close ${r.target_close_date}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-gray-700">${(r.amount_committed || 0).toLocaleString('en-US')}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          (r.status || '').toLowerCase() === 'closed' ? 'bg-emerald-100 text-emerald-700'
                          : (r.status || '').toLowerCase() === 'active' ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-700'
                        }`}>{r.status || '—'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.fundraise_status.investors && data.fundraise_status.investors.length > 0 && (
              <div>
                <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Investors</h4>
                <div className="divide-y divide-gray-100">
                  {data.fundraise_status.investors.map((inv, i) => (
                    <div key={i} className="py-2 flex items-center justify-between text-xs" data-testid={`dr-investor-${i}`}>
                      <div>
                        <p className="font-semibold text-gray-900">{inv.name || '—'}</p>
                        <p className="text-[10px] text-gray-500">{inv.country || ''}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-gray-700">${(inv.amount || 0).toLocaleString('en-US')}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          (inv.status || '').toLowerCase() === 'committed' ? 'bg-emerald-100 text-emerald-700'
                          : (inv.status || '').toLowerCase() === 'active' ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-700'
                        }`}>{inv.status || '—'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Key Metrics */}
        {m && m.mau > 0 && (
          <div id="sec-metrics" className="scroll-mt-16">
            <h3 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3 flex items-center gap-2"><BarChart3 size={12} /> Key Metrics ({m.period})</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'MAU', value: m.mau?.toLocaleString() },
                { label: 'MRR', value: `$${m.mrr?.toLocaleString()}` },
                { label: 'ARR', value: `$${((m.arr || 0) / 1000).toFixed(0)}k` },
                { label: 'Churn', value: `${m.churn_rate}%` },
                { label: 'LTV:CAC', value: `${m.ltv_cac_ratio}x` },
                { label: 'NPS', value: m.nps },
              ].map(k => (
                <div key={k.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
                  <p className="text-2xl font-black text-[#1A1209]">{k.value}</p>
                  <p className="text-[9px] text-gray-500 uppercase font-semibold">{k.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* KPI History for Investors */}
        {isInvestor && data.kpi_history?.length > 1 && (
          <div id="sec-kpi-history" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="kpi-history-chart">
            <h3 className="text-sm font-bold text-[#1A1209] mb-4 flex items-center gap-2"><Activity size={14} className="text-[#7B4FD8]" /> KPI Trend (Live)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {['mau', 'mrr'].map(metric => {
                const values = [...data.kpi_history].reverse().map(r => r[metric] || 0);
                const max = Math.max(...values, 1);
                return (
                  <div key={metric}>
                    <p className="text-[10px] text-gray-500 uppercase font-semibold mb-2">{metric === 'mau' ? 'Monthly Active Users' : 'Monthly Recurring Revenue'}</p>
                    <div className="flex items-end gap-1 h-24 bg-gray-50 rounded-lg p-3">
                      {values.map((v, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                          <div className={`w-full rounded-t ${metric === 'mau' ? 'bg-[#A78BFA]' : 'bg-emerald-400'}`} style={{ height: `${(v / max) * 80}%`, minHeight: '2px' }} />
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-[7px] text-gray-400 mt-1 px-1">
                      {[...data.kpi_history].reverse().map((r, i) => <span key={i}>{r.month?.split(' ')[0]?.slice(0, 3)}</span>)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Revenue Growth */}
        {f.revenue_trajectory?.length > 0 && (
          <div id="sec-revenue" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16">
            <h3 className="text-sm font-bold text-[#1A1209] mb-3 flex items-center gap-2">
              <TrendingUp size={14} className="text-[#7B4FD8]" /> Revenue Trajectory
              {isInvestor && <span className="text-[9px] text-emerald-500 font-semibold ml-auto">Live Data</span>}
            </h3>
            <div className="flex items-end gap-2 h-32">
              {f.revenue_trajectory.map((r, i) => {
                const max = Math.max(...f.revenue_trajectory.map(x => x.revenue), 1);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <p className="text-[8px] text-gray-500 font-semibold">${(r.revenue / 1000).toFixed(0)}k</p>
                    <div className="w-full bg-gradient-to-t from-[#7B4FD8] to-[#A78BFA] rounded-t-lg transition-all" style={{ height: `${(r.revenue / max) * 80}%`, minHeight: '4px' }} />
                    <p className="text-[8px] text-gray-400">{r.month?.split(' ')[0]?.slice(0, 3)}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-3">Total Revenue: <span className="font-bold text-[#1A1209]">${f.total_revenue?.toLocaleString()}</span></p>
          </div>
        )}

        {/* Valuation + Fundraise — unified light card */}
        <div id="sec-valfund" className="grid grid-cols-1 sm:grid-cols-2 gap-4 scroll-mt-16">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-[10px] text-gray-500 uppercase font-bold mb-3">Capital & Valuation</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-[9px] text-gray-500 uppercase font-bold">Valuation</p><p className="text-3xl font-black text-[#5B32C8]">{v.current > 0 ? `$${(v.current / 1000000).toFixed(1)}M` : 'N/A'}</p><p className="text-[10px] text-gray-500 mt-0.5">{v.basis}</p></div>
              <div><p className="text-[9px] text-gray-500 uppercase font-bold">Total Raised</p><p className="text-3xl font-black text-[#1A1209]">{fr.total_raised > 0 ? `$${(fr.total_raised / 1000).toFixed(0)}k` : '$0'}</p><p className="text-[10px] text-gray-500 mt-0.5">{fr.total_raised > 0 ? `${fr.active_investors} investors` : '100% Founder-Funded to date'}</p></div>
            </div>
            {fr.closed_rounds?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                {fr.closed_rounds.map((r, i) => (
                  <div key={i} className="flex justify-between text-xs py-1">
                    <span className="text-gray-500">{r.name}</span>
                    <span className="text-gray-900 font-semibold">${(r.amount / 1000).toFixed(0)}k @ ${(r.post_money / 1000000).toFixed(1)}M</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-sm font-bold text-[#1A1209] mb-3 flex items-center gap-2"><Award size={14} className="text-[#F5831F]" /> Key Milestones</h3>
            <div className="space-y-2">
              {/* Step-4 split: hide the legacy 8-row list when the new
                  Prospect/Investor section is present, to avoid duplication
                  and to enforce the "last 5 only" Prospect rule. */}
              {!(data.milestones_recent_5 || data.milestones_full) && data.milestones?.map((ms, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#F5831F] rounded-full flex-shrink-0" />
                  <span className="text-xs text-gray-700 flex-1">{ms.title}</span>
                  <span className="text-[10px] text-gray-400">{ms.date}</span>
                </div>
              ))}
              {(data.milestones_recent_5 || data.milestones_full) && (
                <p className="text-[11px] text-gray-400 italic">Shown above in the dedicated milestones panel.</p>
              )}
            </div>
            {data.pr_summary?.total_reach > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-[9px] text-gray-500 uppercase">Media Reach</p>
                <p className="text-lg font-black text-[#1A1209]">{(data.pr_summary.total_reach / 1000).toFixed(0)}k+</p>
              </div>
            )}
          </div>
        </div>

        {/* Board */}
        {data.board?.length > 0 && (
          <div id="sec-board" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16">
            <h3 className="text-sm font-bold text-[#1A1209] mb-3 flex items-center gap-2"><Users size={14} className="text-[#7B4FD8]" /> Board & Advisors</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {data.board.map((b, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${b.role === 'independent' ? 'bg-slate-500' : b.role === 'executive' ? 'bg-[#7B4FD8]' : 'bg-gray-400'}`}>{b.name[0]}</div>
                  <div><p className="text-xs font-bold text-gray-900">{b.name}</p><p className="text-[10px] text-gray-500 capitalize">{b.role}{b.committee ? ` — ${b.committee}` : ''}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Active Investor Brief — visible only to investor-tier rooms ─────
            Plain-English implementation guide + investor-relevance analysis
            for the June 4 2026 system deployment. Prospect rooms never see
            this section. */}
        {isInvestor && <div id="sec-brief" className="scroll-mt-16"><ActiveInvestorBrief investorId={data.investor_id} codebaseMetrics={codebase} /></div>}
        {isInvestor && <DataRoomSyndicate token={sessionToken} />}

        {/* Footer */}
        <div className="text-center py-6 border-t border-gray-200">
          <p className="text-[10px] text-gray-400">This document is confidential and intended solely for the named recipient. It does not constitute an offer of securities.</p>
          <p className="text-[10px] text-gray-400 mt-1">HiBFF Ltd. — {new Date().getFullYear()} — hibff.com</p>
        </div>
        </div>
        </div>

        {/* Scroll-to-top */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-5 z-[9000] w-11 h-11 rounded-full bg-[#7B4FD8] text-white shadow-lg hover:bg-[#6a3fc7] flex items-center justify-center"
          aria-label="Scroll to top"
          data-testid="dr-scroll-top"
        >
          <ArrowUp size={18} />
        </button>
      </div>
    </div>
  );
};

export default DataRoom;

// ─── Active Investor Brief ───────────────────────────────────────────────────
// Renders the June 4 2026 system-deployment write-up for institutional
// investors. Two parts:
//   Part 1 — Plain-English implementation guide (5 capability sections).
//   Part 2 — Investor-relevance analysis explaining why each capability
//            insulates equity floor and scales LTV / reduces CAC.
// Visible ONLY when room_type === 'investor' (caller already gates).

const ActiveInvestorBrief = ({ investorId, codebaseMetrics }) => {
  const [open, setOpen] = React.useState(true);
  const [tab, setTab] = React.useState('build'); // 'build' | 'investor' | 'simulator' | 'cost'

  // Telemetry: track which AIB sub-tab the investor is dwelling on. Maps to
  // canonical tab keys used in `useInvestorTelemetry`: active-brief,
  // growth-simulator, cost-replicate. Disabled for prospect rooms (no id).
  const TAB_MAP = {
    build: 'active-brief',
    investor: 'active-brief',
    simulator: 'growth-simulator',
    cost: 'cost-replicate',
  };
  useInvestorTelemetry(investorId || null, TAB_MAP[tab] || 'active-brief', !!investorId);

  // Pull canonical engine inputs (floor, $/user, multiple) from the public
  // snapshot endpoint — same source used by the Active Investor Vault. The
  // simulator below is purely client-side once these inputs are loaded; the
  // VC dragging the slider never writes anything to the backend.
  const [engine, setEngine] = React.useState(null);
  React.useEffect(() => {
    fetch('/api/investors/public-snapshot')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setEngine({
            floor: d.valuation_usd || 11_500_000,
            per_user: d.monthly_per_user_usd || 26.49,
            multiple: d.revenue_multiple || 12,
            anchors: d.user_anchors || [],
          });
        } else {
          setEngine({ floor: 11_500_000, per_user: 26.49, multiple: 12, anchors: [] });
        }
      })
      .catch(() => setEngine({ floor: 11_500_000, per_user: 26.49, multiple: 12, anchors: [] }));

    // Cross-tab broadcast — refetch when founder saves Cap Table
    const onStorage = (e) => {
      if (e.key === 'hibff_engine_v') {
        fetch('/api/investors/public-snapshot').then(r => r.ok ? r.json() : null).then(d => {
          if (d) setEngine({ floor: d.valuation_usd || 11_500_000, per_user: d.monthly_per_user_usd || 26.49, multiple: d.revenue_multiple || 12, anchors: d.user_anchors || [] });
        });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const buildSections = [
    {
      n: 1,
      title: 'Avatar Customization & Core Chat Setup ("AvA" Library)',
      body: [
        'Teens select an AI-generated teen avatar profile and customize the avatar\'s name, character personality, language, and background history.',
        'After "Create My AvA," the system redirects to an introduction page where the chosen avatar introduces itself out loud by name and discusses the challenges ahead.',
        'Subsequent text or voice conversations programmatically reference specific challenges sequentially. The avatar offers custom encouragement text and voice playback with animated imagery (no complex real-time mouth-shape sync required).',
        'A background worker checks platform activity — if a teen has not initialized a chat session for 7 consecutive days, the system triggers an encouraging warm SMS nudge to their registered device.',
      ],
    },
    {
      n: 2,
      title: 'Custom LLM Safety Filters & Dynamic Crisis Triage System',
      body: [
        'Explicit filter layer on the custom LLM wrapper (NVIDIA NeMo Guardrails) instantly evaluates all incoming text and voice. The filter blocks prompts relating to self-harm, hate speech, medical queries, or mental health diagnostics.',
        'Real-time safety classification model tracks score parameters between 0.00 and 1.00.',
        'Score ≥ 0.80 (Self-Harm Risk): the avatar redirects the teen to speak to a real person, dynamically displaying official support numbers for the US and UK.',
        'Score ≥ 0.95 (Critical / Suicidal Risk): standard chatbot operations halt immediately and a 3-step automated routing protocol cleanly transfers the session state to live emergency support configurations (e.g., The Samaritans).',
        'Founder Cockpit Alert: any crisis event ≥ 0.80 dispatches an automated high-priority SMS and email alert directly to the CEO via backend webhook.',
      ],
    },
    {
      n: 3,
      title: '"Maya" Spoken Onboarding & Psychometric Challenge Generation',
      body: [
        'Initial registration pipeline is spoken out loud by system avatar Maya.',
        'Maya guides the user through 22 distinct audio-visual data fields: 12 formal BIG5 / OCEAN personality questions + 10 localized personal preference questions (likes / hobbies).',
        'These 22 fields feed the backend challenge algorithm. The system assembles tailor-made challenges using the psychometric data — no two user challenge pipelines look structurally identical.',
        'The 10 preference data strings are used as seeds for the automated generation of custom virtual badges and physical NFT rewards that populate the 180-day countdown lock mechanism.',
      ],
    },
    {
      n: 4,
      title: 'Schools Licensing Infrastructure Control',
      body: [
        'Standalone low-friction application form on the public web app (/schools) where schools worldwide apply for enrolment.',
        'Special institutional pricing quoted per school (call for your rate), with a 14-day free pilot window — cohort size and terms negotiated per school after the trial.',
        'Approved schools send pre-built email invite templates to parents. The teen and parent execute their joint charter document online, allowing the teen to complete challenges off-hours.',
        'Reporting architecture isolates student metrics. The school interface manages macro-enrollments at a distance and is strictly locked out from viewing individual monthly teen performance reports routed to parents.',
        'Parents introduced through a licensed school register directly on HiBFF.com for the standard £28/month (UK) membership — the school acts purely as a trusted, authoritative referral channel.',
        'Every newly submitted school profile appends a log entry to the Schools Programme Engine (/admin/schools-engine) and sends an email update to the CEO.',
      ],
    },
    {
      n: 5,
      title: 'Subscription Cooldown & Indefinite Legacy Account Playback',
      body: [
        'Explicit alternative button pathway on the cancellation page loop labelled "Pause Subscription."',
        'Users may choose a temporary 90-day cooling-off pause before executing a full hard account deletion.',
        'Alternatively, parents can toggle an "Indefinite Subscription Pause." When active, monthly recurring billing drops to $0 but the account database state does not purge.',
        'The minor preserves lookup access to view past text histories with their custom avatars and maintains read/write connectivity inside their teen-led safe socialization peer circles.',
      ],
    },
  ];

  const investorSections = [
    {
      title: '1. Complete Regulatory Protection (Insulating the 12× Cap Table Multiple)',
      tagline: 'Engineering out corporate negligence risk.',
      body: 'Minor-focused software assets face intense regulatory scrutiny globally regarding data management and safety. The custom Claude Sonnet 4.5 safety wrapper, automated 0.80 / 0.95 crisis triggers, and external helpline routing transform the application from a generic communication app into an institutional-grade, liability-free wellness asset. This justifies the illustrative 12× revenue multiplier.',
    },
    {
      title: '2. Programmatic Proof of Low Customer Acquisition Costs (CAC)',
      tagline: 'An unreplicable, hyper-efficient distribution engine.',
      body: 'The biggest cash-burn for early SaaS platforms is consumer marketing spend. The HiBFF Schools licensing protocol turns educational systems into the primary sales force: each licensed school (on special institutional pricing, quoted per school) introduces the brand directly to families with maximum institutional authority, driving localized parents into the £28/mo UK price ($35.99 US) ecosystem at a near-zero cost of customer acquisition.',
    },
    {
      title: '3. Neutralizing SaaS Churn through Structural Data Moats',
      tagline: 'Permanent switching costs built into the data structure.',
      body: 'Traditional youth apps lose users rapidly once novelty fades. A teen cannot easily switch to a competitor without throwing away a highly personalized psychometric identity built by Maya. Furthermore, the 90-day cooling pause and indefinite history playback mean that even when a family stops actively paying, the platform never loses custody of the user data or network effects. Paused users continue interacting within their safe teen peer circles — ensuring a high-probability reactivation pipeline that protects long-term Customer Lifetime Value (LTV).',
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="active-investor-brief">
      <button
        onClick={() => setOpen(s => !s)}
        className="w-full flex items-center justify-between px-6 py-5 bg-gradient-to-r from-indigo-900 via-purple-900 to-fuchsia-900 text-white"
        data-testid="aib-toggle">
        <div className="text-left">
          <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-200">Active Investor Brief — Confidential</div>
          <div className="text-base font-black tracking-tight mt-1">Developer Implementation Guide & Investor Relevance Analysis</div>
          <div className="text-[11px] text-indigo-200 mt-1">June 4 2026 system deployment · Plain-English engineering + capital-market read-through</div>
        </div>
        <span className="text-xs font-bold uppercase tracking-wider bg-white/15 px-3 py-1.5 rounded-full">{open ? '× Collapse' : '▾ Expand'}</span>
      </button>

      {open && (
        <div className="p-6 space-y-4">
          {/* Tab switcher */}
          <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50" data-testid="aib-tabs">
            <button onClick={() => setTab('build')}
              className={`px-4 py-1.5 text-xs font-bold rounded-md transition ${tab === 'build' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
              data-testid="aib-tab-build">
              Part 1 — What&apos;s Built
            </button>
            <button onClick={() => setTab('investor')}
              className={`px-4 py-1.5 text-xs font-bold rounded-md transition ${tab === 'investor' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
              data-testid="aib-tab-investor">
              Part 2 — Why It Matters for Your Equity
            </button>
            <button onClick={() => setTab('simulator')}
              className={`px-4 py-1.5 text-xs font-bold rounded-md transition ${tab === 'simulator' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
              data-testid="aib-tab-simulator">
              Growth Simulator
            </button>
            <button onClick={() => setTab('cost')}
              className={`px-4 py-1.5 text-xs font-bold rounded-md transition ${tab === 'cost' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
              data-testid="aib-tab-cost">
              Build vs Replicate
            </button>
          </div>

          {tab === 'build' && (
            <div className="space-y-4" data-testid="aib-build-panel">
              <p className="text-xs text-gray-600 leading-relaxed">
                The following is a plain-English account of what the HiBFF engineering build delivers today. It is provided to active investors as a transparency layer behind the Dynamic Valuation Engine numbers you see elsewhere in this Data Room.
              </p>
              {buildSections.map(s => (
                <div key={s.n} className="border-l-2 border-indigo-300 pl-4 py-2" data-testid={`aib-build-section-${s.n}`}>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">{s.n}. {s.title}</h4>
                  <ul className="space-y-1.5">
                    {s.body.map((p, idx) => (
                      <li key={idx} className="text-xs text-gray-700 leading-relaxed flex gap-2">
                        <span className="text-indigo-400 flex-shrink-0 mt-1">•</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {tab === 'investor' && (
            <div className="space-y-4" data-testid="aib-investor-panel">
              <p className="text-xs text-gray-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-lg p-3">
                In early-stage venture financing, technical complexity alone does not attract capital — the <strong>elimination of systemic risk</strong> does. The June 4 2026 deployment hardcodes safety protocols, multi-modal avatars, and automated triage thresholds directly into the asset engine, providing active investors with ironclad proof of enterprise defense.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="aib-investor-pillars">
                {[
                  { t: 'Legal De-Risking', s: 'NeMo Guardrails & automated crisis loops insulate from liability.' },
                  { t: 'Drastic CAC Reduction', s: '"HiBFF Gives" acts as an organic B2B2C parent acquisition loop.' },
                  { t: 'Long-Term LTV Defense', s: '90-day cooling pauses & historical archives neutralize standard churn.' },
                ].map((p, i) => (
                  <div key={i} className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 mb-1">{p.t}</div>
                    <div className="text-xs text-gray-700 leading-relaxed">{p.s}</div>
                  </div>
                ))}
              </div>
              {investorSections.map((s, i) => (
                <div key={i} className="border-l-2 border-emerald-300 pl-4 py-2" data-testid={`aib-investor-section-${i}`}>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">{s.title}</h4>
                  <p className="text-[11px] text-emerald-700 italic mb-2">{s.tagline}</p>
                  <p className="text-xs text-gray-700 leading-relaxed">{s.body}</p>
                </div>
              ))}
              <div className="bg-gradient-to-r from-indigo-50 to-fuchsia-50 border border-indigo-200 rounded-lg p-4">
                <h4 className="text-sm font-bold text-gray-900 mb-1.5">Summary for Active Portal Users</h4>
                <p className="text-xs text-gray-700 leading-relaxed">
                  This June 4 2026 build bridges engineering logic with capital market realities. When you open the Dynamic Valuation Engine card in the Cap Table view, you are not looking at a hypothetical projection sheet — you are looking at a highly defensive, legally insulated infrastructure that protects your equity floor while paving an explicit, calculated path toward a multi-million dollar software scale.
                </p>
              </div>
            </div>
          )}

          {tab === 'simulator' && <SimulatorPanel engine={engine} />}
          {tab === 'cost' && <CostAnalysisPanel metrics={codebaseMetrics} />}

          <p className="text-[10px] text-gray-400 leading-relaxed pt-3 border-t border-gray-100">
            Confidential. Visible only inside investor-tier Data Rooms. Not an offer of securities. Not financial guidance. Information may be updated as the build evolves.
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Growth Simulator Panel ──────────────────────────────────────────────────
// Pure client-side "what-if" the VC can drag from 0 → 5M users. Reads floor
// + $/user + multiple from the engine prop (sourced from the public snapshot)
// — never invents numbers. Floor stays clamped until the revenue equation
// crosses it; then V_E rockets up live.

const SimulatorPanel = ({ engine }) => {
  // Default subscriber count for shareable link: 500k — the "Market Lead"
  // anchor — so investors land on a meaningful revenue-scaled valuation
  // instead of the floor. They can still drag down to 0 to see the floor.
  const [u, setU] = React.useState(500_000);
  if (!engine) return <div className="py-6 text-xs text-gray-500" data-testid="aib-sim-loading">Loading engine…</div>;

  const { floor, per_user, multiple } = engine;
  const mrr = u * per_user;
  const arr = mrr * 12;
  const raw_val = arr * multiple;
  const ent_val = Math.max(floor, raw_val);
  const is_floor = ent_val <= floor;
  // Pivot point: smallest U where U × per_user × 12 × multiple > floor
  const pivot = per_user * multiple > 0 ? Math.ceil(floor / (per_user * 12 * multiple)) + 1 : 0;
  const SLIDER_MAX = 5_000_000;

  const fmtMoney = (n) => n >= 1e9 ? `$${(n/1e9).toFixed(2)}B` : n >= 1e6 ? `$${(n/1e6).toFixed(2)}M` : n >= 1e3 ? `$${(n/1e3).toFixed(0)}k` : `$${Number(n).toLocaleString()}`;
  const fmtFull = (n) => `$${Number(n).toLocaleString()}`;
  const fmtN = (n) => Number(n).toLocaleString();

  // Slider thumb percent for visual pivot marker
  const pct = Math.min(100, (u / SLIDER_MAX) * 100);
  const pivotPct = Math.min(100, (pivot / SLIDER_MAX) * 100);

  return (
    <div className="space-y-5" data-testid="aib-simulator-panel">
      <p className="text-xs text-gray-700 leading-relaxed bg-indigo-50 border border-indigo-200 rounded-lg p-3">
        Drag the slider to step the active subscriber count from <strong>0</strong> up past the convergence velocity point at <strong>{fmtN(pivot)}</strong>. Watch the floor stay rigidly locked at <strong>{fmtFull(floor)}</strong> until the revenue equation surpasses it — then the engine unclamps and scales upward live.
      </p>

      {/* Big V_E hero */}
      <div className={`rounded-2xl p-6 text-white shadow-xl ${is_floor ? 'bg-gradient-to-br from-amber-700 to-amber-900' : 'bg-gradient-to-br from-emerald-700 via-teal-700 to-emerald-900'} transition-colors duration-500`} data-testid="aib-sim-hero">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">Benchmark Valuation</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${is_floor ? 'bg-amber-300/30' : 'bg-emerald-300/30'}`}>
            {is_floor ? 'Floor Active' : 'Revenue-Scaled'}
          </span>
        </div>
        <div className="text-5xl font-black tracking-tight mb-2" data-testid="aib-sim-ve">{fmtFull(ent_val)}</div>
        <div className="text-xs opacity-80">{fmtMoney(ent_val)} · at U = {fmtN(u)} active subscribers</div>
      </div>

      {/* Slider */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Active Subscribers (U)</label>
          <span className="text-sm font-black text-indigo-700" data-testid="aib-sim-u-readout">{fmtN(u)}</span>
        </div>
        {/* Pivot marker overlay */}
        <div className="relative">
          <input
            type="range" min="0" max={SLIDER_MAX} step="100" value={u}
            onChange={e => setU(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            data-testid="aib-sim-slider"
          />
          {/* Pivot tick */}
          <div className="absolute -top-1 pointer-events-none" style={{ left: `calc(${pivotPct}% - 1px)` }}>
            <div className="w-0.5 h-4 bg-emerald-500" />
            <div className="text-[8px] font-bold text-emerald-700 -ml-3 mt-0.5 whitespace-nowrap">Pivot {fmtN(pivot)}</div>
          </div>
          {/* Current position label below */}
          <div className="absolute top-3 pointer-events-none" style={{ left: `calc(${pct}% - 12px)` }}>
            <div className="text-[8px] font-bold text-indigo-700">▲</div>
          </div>
        </div>
        <div className="flex justify-between text-[9px] text-gray-400 mt-6">
          <span>0</span><span>1M</span><span>2M</span><span>3M</span><span>4M</span><span>5M</span>
        </div>
        {/* Each anchor button gets its own colour so the milestone
            sequence reads as a visual ladder (slate → emerald → teal →
            sky → indigo → violet → fuchsia → rose). Highlights the
            currently-selected anchor with a deeper ring. */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {[
            { v: 0,         label: '0',         cls: 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-300',           ring: 'ring-slate-500' },
            { v: pivot,     label: fmtN(pivot), cls: 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border-emerald-300',   ring: 'ring-emerald-500' },
            { v: 10_000,    label: '10,000',    cls: 'bg-teal-100 hover:bg-teal-200 text-teal-800 border-teal-300',               ring: 'ring-teal-500' },
            { v: 50_000,    label: '50,000',    cls: 'bg-sky-100 hover:bg-sky-200 text-sky-800 border-sky-300',                   ring: 'ring-sky-500' },
            { v: 175_000,   label: '175,000',   cls: 'bg-indigo-100 hover:bg-indigo-200 text-indigo-800 border-indigo-300',       ring: 'ring-indigo-500' },
            { v: 500_000,   label: '500,000',   cls: 'bg-violet-100 hover:bg-violet-200 text-violet-800 border-violet-300',       ring: 'ring-violet-500' },
            { v: 1_000_000, label: '1,000,000', cls: 'bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-800 border-fuchsia-300',   ring: 'ring-fuchsia-500' },
            { v: 5_000_000, label: '5,000,000', cls: 'bg-rose-100 hover:bg-rose-200 text-rose-800 border-rose-300',               ring: 'ring-rose-500' },
          ].map(({ v, label, cls, ring }) => {
            const active = u === v;
            return (
              <button
                key={v}
                onClick={() => setU(v)}
                className={`px-2.5 py-1 text-[10px] font-bold rounded border transition ${cls} ${active ? `ring-2 ${ring} shadow-sm` : ''}`}
                data-testid={`aib-sim-jump-${v}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Math breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3" data-testid="aib-sim-mrr">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">MRR</div>
          <div className="text-lg font-bold text-gray-900">{fmtMoney(mrr)}</div>
          <div className="text-[9px] text-gray-500">U × ${per_user} blended</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3" data-testid="aib-sim-arr">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">ARR</div>
          <div className="text-lg font-bold text-gray-900">{fmtMoney(arr)}</div>
          <div className="text-[9px] text-gray-500">MRR × 12</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3" data-testid="aib-sim-raw">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Raw × {multiple}×</div>
          <div className="text-lg font-bold text-gray-900">{fmtMoney(raw_val)}</div>
          <div className="text-[9px] text-gray-500">ARR × multiple</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="aib-sim-floor">
          <div className="text-[10px] uppercase tracking-wider text-amber-700">Asset Floor</div>
          <div className="text-lg font-bold text-amber-900">{fmtMoney(floor)}</div>
          <div className="text-[9px] text-amber-600">Intrinsic moat</div>
        </div>
      </div>

      <p className="text-[10px] text-gray-500 leading-relaxed">
        Simulator inputs are read live from the canonical Cap Table — if the founder updates the per-user price, the multiple, or the asset floor, this simulator updates automatically. The numbers shown are not a forecast or an offer; they are deterministic outputs of the published formula.
      </p>
    </div>
  );
};

// ─── Cost Analysis Panel ─────────────────────────────────────────────────────
// "Cost to Produce vs Cost to Replicate" — institutional due-diligence read.
// All numbers come from the founder document; this is content-only, no API.

const CostAnalysisPanel = ({ metrics }) => {
  const liveLines = metrics?.totals?.everything?.lines ? `${Math.round(metrics.totals.everything.lines / 1000)}k` : '300k+';
  const liveEndpoints = metrics?.live_endpoints ? Number(metrics.live_endpoints).toLocaleString() : '1,200+';
  const produceItems = [
    {
      title: 'Core Software Architecture',
      low: 1_400_000, high: 1_800_000,
      desc: 'Building 843 distinct endpoints means a highly modular microservices network. Writing, documentation, and automated testing for an original code asset of this scale typically consumes 15,000 to 18,000 senior engineering hours.',
    },
    {
      title: 'AI Alignment, Safety & Prompts',
      low: 450_000, high: 600_000,
      desc: 'Not a simple integration — it is an isolated behavioural environment. Configuring a custom Claude Sonnet 4.5 runtime to execute real-time BIG5/OCEAN psychometric evaluation, paired with NVIDIA NeMo Guardrails that dynamically block self-harm, hate speech, medical, or mental-health content, requires continuous adversarial training ("red-teaming") and logic validation before deployment.',
    },
    {
      title: 'Compliance Engineering',
      low: 150_000, high: 250_000,
      desc: 'Hard-coding the codebase to pass strict SOC2 validation and upgraded COPPA (minor-safeguarding) privacy protocols from the very first line of code requires highly complex data ringfencing — custom data silos, immutable audit logs, and parent-controlled pause/data custody storage structures rather than out-of-the-box user databases.',
    },
    {
      title: 'Product R&D & Psychometric Design',
      low: 200_000, high: 350_000,
      desc: "Mapping out Maya's 22 spoken onboarding indicators and engineering an algorithm that dynamically formats completely distinct sets of long-term text, voice, and 180-day countdown rewards (via Crossmint / Base / Polygon) represents a massive behavioural research phase.",
    },
  ];

  const replicateItems = [
    {
      title: 'Raw Engineering Replication',
      low: 750_000, high: 1_200_000,
      desc: `Re-writing ${liveLines} lines of modular code across a dual-interface architecture would require an elite team of 5–7 senior backend, frontend, and AI engineers working for roughly 9 to 12 months at standard market agency billing.`,
    },
    {
      title: 'Compliance Replication',
      low: 150_000, high: 300_000,
      desc: 'Retrofitting software to meet COPPA data-purging limits and auditing the infrastructure for SOC2 readiness adds immediate engineering and audit-platform integration fees.',
    },
    {
      title: 'AI Triage Layer Replication',
      low: 250_000, high: 250_000,
      desc: 'Recreating the proprietary crisis scoring engine (≥ 0.80 self-harm / ≥ 0.95 suicidal triggers) with automated multi-region 3-step routing to localised helplines takes extensive testing to ensure zero system failures. Budget for pure risk-reduction safety logic.',
    },
  ];

  const valueFactors = [
    { title: 'The Patent Wall', body: 'A copycat cannot simply copy the platform. Three live UK patent applications — GB2617027.4 (Safety-Gated AI Social Skills Practice), GB2622892.4 (Fail-Safe Escalation & Warm Handover) and GB2622997.1 (Guardian-Gated Custody of Minors\u2019 Digital Assets) — cover the 180-day countdown reward vaults, joint family charters, and psychometric challenge engines, with four further applications in preparation post-investment.' },
    { title: 'Elimination of Time Risk (24-Month Compression)', body: 'For a VC, capital is cheap but time is expensive. Replicating the tech stack takes a competitor at least 12 to 18 months of quiet development. In that same window, the live HiBFF platform leverages the HiBFF Gives school network to capture thousands of discounted consumer subscriptions across entire town regions.' },
    { title: 'The Revenue Velocity Guarantee', body: 'The 180-day countdown vault means every customer acquisition automatically locks in a minimum 6-month lifecycle value ($159 contracted LTV at the $26.49 blended rate). A clone app starting from scratch faces high subscriber churn, while this system architecture structurally guarantees retention.' },
  ];

  const fmtRange = (low, high) => low === high ? `$${(low/1_000_000).toFixed(2)}M` : `$${(low/1_000_000).toFixed(2)}M – $${(high/1_000_000).toFixed(2)}M`;
  const sumLow = (arr) => arr.reduce((s, x) => s + x.low, 0);
  const sumHigh = (arr) => arr.reduce((s, x) => s + x.high, 0);

  return (
    <div className="space-y-5" data-testid="aib-cost-panel">
      <p className="text-xs text-gray-700 leading-relaxed bg-indigo-50 border border-indigo-200 rounded-lg p-3">
        Calculating the <strong>Cost to Produce</strong> vs the <strong>Cost to Replicate</strong> HiBFF.com reveals two very different figures. Producing a highly regulated, secure social platform from scratch involves heavy trial, error, architectural adjustments, and R&D. Replication assumes a competitor already possesses the perfect, finished blueprint and is simply paying for the raw engineering hours to reconstruct it.
      </p>

      {/* ── 1. Cost to Produce ─────────────────────────────────────── */}
      <div data-testid="cost-produce-section">
        <div className="flex items-baseline justify-between mb-3">
          <h4 className="text-sm font-black text-gray-900">1. Cost to Produce</h4>
          <span className="text-[11px] text-gray-500 italic">What you actually invested</span>
        </div>
        <p className="text-xs text-gray-600 leading-relaxed mb-3">
          {liveLines} lines of code · {liveEndpoints} live API endpoints (recounted live in the Codebase panel) · custom-isolated Claude Sonnet 4.5 with live script-blocking guardrails · multi-modal avatar selection · dual-dashboard parent/teen coordination logs. The development history reflects a high-end enterprise build cycle.
        </p>
        <div className="space-y-2">
          {produceItems.map((it, i) => (
            <div key={i} className="border-l-2 border-amber-300 pl-4 py-2 bg-amber-50/30" data-testid={`cost-produce-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-gray-900">{it.title}</span>
                <span className="text-xs font-bold text-amber-700">{fmtRange(it.low, it.high)}</span>
              </div>
              <p className="text-[11px] text-gray-600 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 bg-amber-100 border border-amber-300 rounded-lg p-3 flex items-center justify-between" data-testid="cost-produce-total">
          <span className="text-xs font-bold uppercase tracking-wider text-amber-900">Total Estimated Cost to Produce</span>
          <span className="text-base font-black text-amber-900">{fmtRange(sumLow(produceItems), sumHigh(produceItems))}</span>
        </div>
      </div>

      {/* ── 2. Cost to Replicate ────────────────────────────────────── */}
      <div data-testid="cost-replicate-section">
        <div className="flex items-baseline justify-between mb-3">
          <h4 className="text-sm font-black text-gray-900">2. Cost to Replicate</h4>
          <span className="text-[11px] text-gray-500 italic">The &quot;copycat&quot; cost</span>
        </div>
        <p className="text-xs text-gray-600 leading-relaxed mb-3">
          A well-funded competitor who reviewed this active investor data room would save money on initial research and product discovery because the system design serves as their cheat sheet. But they hit a wall reconstructing the tech stack:
        </p>
        <div className="bg-gray-900 text-indigo-100 rounded-lg px-4 py-2 font-mono text-[11px] text-center mb-3" data-testid="cost-replicate-formula">
          [Raw Engineering Blocks] + [Regulatory Hardening] + [Legal / IP Barriers] = Cost to Replicate
        </div>
        <div className="space-y-2">
          {replicateItems.map((it, i) => (
            <div key={i} className="border-l-2 border-rose-300 pl-4 py-2 bg-rose-50/30" data-testid={`cost-replicate-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-gray-900">{it.title}</span>
                <span className="text-xs font-bold text-rose-700">{fmtRange(it.low, it.high)}</span>
              </div>
              <p className="text-[11px] text-gray-600 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 bg-rose-100 border border-rose-300 rounded-lg p-3 flex items-center justify-between" data-testid="cost-replicate-total">
          <span className="text-xs font-bold uppercase tracking-wider text-rose-900">Total Estimated Technical Cost to Replicate</span>
          <span className="text-base font-black text-rose-900">{fmtRange(sumLow(replicateItems), sumHigh(replicateItems))}</span>
        </div>
      </div>

      {/* ── 3. Why Cost to Replicate Benefits Active Investors ───── */}
      <div data-testid="cost-benefit-section">
        <div className="flex items-baseline justify-between mb-3">
          <h4 className="text-sm font-black text-gray-900">3. Why &quot;Cost to Replicate&quot; Benefits Active Investors</h4>
          <span className="text-[11px] text-gray-500 italic">Time-to-Market Capital Value + Defensive IP Moat</span>
        </div>
        <p className="text-xs text-gray-600 leading-relaxed mb-3">
          When an institutional investor views the $11.5M floor valuation, they perform this exact build-vs-buy calculation. Even if the pure software-engineering replication cost sits closer to $1.75M, the $11.5M baseline is fully justified by the following value-added factors:
        </p>
        <div className="grid grid-cols-1 gap-2">
          {valueFactors.map((v, i) => (
            <div key={i} className="border-l-2 border-emerald-400 pl-4 py-2 bg-emerald-50/30" data-testid={`cost-benefit-${i}`}>
              <div className="text-xs font-bold text-gray-900 mb-1">{v.title}</div>
              <p className="text-[11px] text-gray-700 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Summary ─────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-fuchsia-900 rounded-xl p-5 text-white" data-testid="cost-summary">
        <h4 className="text-sm font-black tracking-tight mb-2">Summary</h4>
        <p className="text-xs leading-relaxed text-indigo-100">
          If a technology buyer tried to reproduce HiBFF without the existing files, they would easily spend <strong>$2M+ in development capital</strong>, run face-first into <strong>three live UK patent applications (GB2617027.4 · GB2622892.4 · GB2622997.1) plus four further applications in preparation</strong>, and lose <strong>24 months of market velocity</strong>. That is exactly why the $11.5M floor represents the ultimate de-risked starting point for an institutional investor.
        </p>
      </div>
    </div>
  );
};
